import type { PasswordEncoder } from './index.ts';

export abstract class AbstractPasswordEncoder implements PasswordEncoder {
    async encode(rawPassword?: string): Promise<string | undefined> {
        if (rawPassword === undefined || rawPassword === null) {
            return undefined
        }
        return await this.encodeDefinedPassword(rawPassword.toString());
    }

    abstract encodeDefinedPassword(rawPassword: string): Promise<string>;

    async matches(rawPassword?: string, encodedPassword?: string): Promise<boolean> {
        if (!rawPassword || !encodedPassword) {
            return false;
        }
        return await this.matchesDefined(rawPassword.toString(), encodedPassword);
    }

    abstract matchesDefined(rawPassword: string, encodedPassword: string): Promise<boolean>;


    upgradeEncoding(encodedPassword?: string): boolean {
        if (!encodedPassword) {
            return false;
        }
        return this.upgradeEncodingDefined(encodedPassword);
    }

    protected upgradeEncodingDefined(encodedPassword: string): boolean {
        return false;
    }

}

export class DelegatingPasswordEncoder extends AbstractPasswordEncoder {
    static DEFAULT_ID_PREFIX = '{'; // {
    static DEFAULT_ID_SUFFIX = '}'; // }
    readonly #idPrefix: string;
    readonly #idSuffix: string;
    readonly #idForEncode: string;
    readonly #encoderForEncode: PasswordEncoder;
    readonly #encoders: Map<string, PasswordEncoder>

    #defaultEncoderForMatches: PasswordEncoder = new class extends AbstractPasswordEncoder {
        readonly #outer: DelegatingPasswordEncoder;
        constructor(outer: DelegatingPasswordEncoder) {
            super();
            this.#outer = outer;
        }
        async encodeDefinedPassword(rawPassword: string): Promise<string> {
            throw new Error('encode is not supported');
        }

        async matchesDefined(rawPassword: string, prefixEncodedPassword: string): Promise<boolean> {
            const id = this.#outer.#extractId(prefixEncodedPassword);
            if (!id) {
                throw new Error(`No password encoder mapped for id ${id}`);
            }
            if (prefixEncodedPassword) {
                const start = prefixEncodedPassword.indexOf(this.#outer.#idPrefix);
                const end = prefixEncodedPassword.indexOf(this.#outer.#idSuffix, start + this.#outer.#idPrefix.length);
                if (start === -1 && end === -1) {
                    throw new Error(`No prefix found in encoded password`);
                }
            }
            throw new Error(`malformed password encoder prefix`);
        }
    }(this);

    constructor(idForEncode: string, encoders: Map<string, PasswordEncoder>, idPrefix:string = DelegatingPasswordEncoder.DEFAULT_ID_PREFIX, idSuffix: string = DelegatingPasswordEncoder.DEFAULT_ID_SUFFIX) {
        if (idForEncode === undefined || idForEncode === null) {
            throw new Error('idForEncode cannot be null or undefined');
        }
        if (idPrefix === undefined || idPrefix === null) {
            throw new Error(`idPrefix cannot be null or undefined`);
        }
        if (!idSuffix) {
            throw new Error(`idSuffix cannot be empty`);
        }
        if (idPrefix.indexOf(idSuffix) !== -1) {
            throw new Error(`idPrefix "${idPrefix}" cannot contain idSuffix "${idSuffix}"`);
        }
        if (!encoders.has(idForEncode)) {
            throw new Error(`No PasswordEncoder mapped for id "${idForEncode}"`);
        }
        for (const id of encoders.keys()) {
            if (id === null) {
                continue;
            }
            if (idPrefix && id.includes(idPrefix)) {
                throw new Error(`id "${id}" cannot include ${idPrefix}`);
            }
            if (idSuffix && id.includes(idSuffix)) {
                throw new Error(`id "${id}" cannot include ${idSuffix}`);
            }
        }
        super();
        this.#idForEncode = idForEncode;
        this.#encoderForEncode = encoders.get(idForEncode)!;
        this.#encoders = new Map(encoders);
        this.#idPrefix = idPrefix;
        this.#idSuffix = idSuffix;
    }

    set defaultPasswordEncoderForMatches(encoder: PasswordEncoder) {
        if (encoder === null || encoder === undefined) {
            throw new Error('defaultPasswordEncoderForMatches cannot be null or undefined');
        }
        this.#defaultEncoderForMatches = encoder;
    }

    async encodeDefinedPassword(rawPassword: string): Promise<string> {
        const encoded = await this.#encoderForEncode.encode(rawPassword);
        return `${this.#idPrefix}${this.#idForEncode}${this.#idSuffix}${encoded}`;
    }

    async matchesDefined(rawPassword: string, prefixEncodedPassword: string): Promise<boolean> {
        const id = this.#extractId(prefixEncodedPassword);
        const delegate = id ? this.#encoders.get(id) : undefined;
        if (delegate === undefined) {
            return await this.#defaultEncoderForMatches.matches(rawPassword, prefixEncodedPassword);
        }
        else {
            const encodedPassword = this.#extractEncodedPassword(prefixEncodedPassword);
            return await delegate.matches(rawPassword, encodedPassword);
        }
    }

    #extractId(encodedPassword?: string): string | undefined {
        if (encodedPassword === undefined) {
            return undefined;
        }
        const start = encodedPassword.indexOf(this.#idPrefix);
        if (start !== 0) {
            return undefined;
        }
        const end = encodedPassword.indexOf(this.#idSuffix, start + this.#idPrefix.length);
        if (end === -1) {
            return undefined;
        }
        return encodedPassword.substring(start + this.#idPrefix.length, end);
    }

    protected upgradeEncodingDefined(prefixEncodedPassword: string): boolean {
        const id = this.#extractId(prefixEncodedPassword);
        if (this.#idForEncode !== id) {
            // todo case-insensitive compare?
            return true;
        }
        else {
            const encodedPassword = this.#extractEncodedPassword(prefixEncodedPassword);
            return this.#encoderForEncode.upgradeEncoding?.(encodedPassword) ?? false;
        }
    }

    #extractEncodedPassword(prefixEncodedPassword: string) {
        const start = prefixEncodedPassword.indexOf(this.#idSuffix);
        return prefixEncodedPassword.substring(start + this.#idSuffix.length);
    }

}

export class NoopPasswordEncoder extends AbstractPasswordEncoder {
    static readonly #INSTANCE = new NoopPasswordEncoder();

    static get instance(): NoopPasswordEncoder {
        return NoopPasswordEncoder.#INSTANCE;
    }

    private constructor() {
        super();
    }

    async encodeDefinedPassword(rawPassword: string): Promise<string> {
        return rawPassword.toString();
    }

    async matchesDefined(rawPassword: string, encodedPassword: string): Promise<boolean> {
        return rawPassword.toString() === encodedPassword;
    }
}
